/**
 * Life Is Golden — PDP "Complete the set" featured pairing.
 *
 * One curated pairing rendered as a compact card with inline Color/Size selection.
 * The shopper picks options and adds straight to cart WITHOUT leaving the page.
 *
 * Reuses the theme's own cart pipeline (mirrors assets/product-form.js and the
 * cart-drawer upsell): explicit JSON POST to Theme.routes.cart_add_url -> refetch
 * /cart.js for the full cart object -> dispatch CartAddEvent on `document`, then
 * explicitly open the cart drawer so the add is visibly confirmed. No reload, no modal.
 *
 * Variant resolution is local: the block embeds the product's variants as JSON and
 * we map the selected option values (option1/2/3) to a variant id, keeping the add
 * button's data-variant-id in sync. The cart drawer is untouched by this element.
 */
import { CartAddEvent, CartErrorEvent } from '@theme/events';

class LigPairFeature extends HTMLElement {
  connectedCallback() {
    try {
      const node = this.querySelector('[data-pair-variants]');
      this.variants = node ? JSON.parse(node.textContent) : [];
    } catch (error) {
      this.variants = [];
    }
    this.addButton = this.querySelector('[data-pair-add]');
    this.selects = Array.from(this.querySelectorAll('[data-pair-option]'));
    this.selects.forEach((select) => select.addEventListener('change', this.#sync));
    this.addEventListener('click', this.#handleClick);
    this.#sync();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  /** Resolve the variant matching the currently-selected option values. */
  #currentVariant() {
    if (!this.variants.length) return null;
    if (!this.selects.length) {
      return this.variants.find((variant) => variant.available) || this.variants[0];
    }
    const selected = this.selects.map((select) => select.value);
    return (
      this.variants.find(
        (variant) =>
          (selected[0] == null || variant.option1 === selected[0]) &&
          (selected[1] == null || variant.option2 === selected[1]) &&
          (selected[2] == null || variant.option3 === selected[2])
      ) || null
    );
  }

  /** Keep the add button's variant id, enabled state and label in sync with the selects. */
  #sync = () => {
    if (!this.addButton) return;
    const labelEl = this.addButton.querySelector('.lig-pair__add-label');
    const variant = this.#currentVariant();

    if (!variant) {
      this.addButton.disabled = true;
      this.addButton.dataset.variantId = '';
      if (labelEl) labelEl.textContent = 'Unavailable';
      return;
    }

    this.addButton.dataset.variantId = String(variant.id);
    if (variant.available === false) {
      this.addButton.disabled = true;
      if (labelEl) labelEl.textContent = 'Sold out';
    } else {
      this.addButton.disabled = false;
      if (labelEl && labelEl.dataset.default) labelEl.textContent = labelEl.dataset.default;
    }
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('[data-pair-add]');
    if (!(button instanceof HTMLButtonElement)) return;
    event.preventDefault();
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled || button.getAttribute('aria-busy') === 'true') return;
    this.#add(variantId, button);
  };

  #cartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((node) => node.getAttribute('data-section-id'))
      .filter(Boolean)
      .join(',');
  }

  async #fetchCart() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      return response.ok ? await response.json() : null;
    } catch (error) {
      return null;
    }
  }

  /** Open Horizon's cart drawer (CartAddEvent morphs its contents but doesn't open it). */
  #openCartDrawer() {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      try {
        drawer.open();
      } catch (error) {
        /* no-op: a closed/absent drawer should never break the add */
      }
    }
  }

  /**
   * Add the selected variant and trigger Horizon's native cart morph/open,
   * mirroring the cart-drawer upsell's #addVariant exactly.
   * @param {string} variantId
   * @param {HTMLButtonElement} button
   */
  async #add(variantId, button) {
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;

    const sections = this.#cartSectionIds();
    const body = JSON.stringify({
      items: [{ id: Number(variantId) || variantId, quantity: 1 }],
      sections,
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body,
      });
      const responseText = await response.text();

      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error('Unexpected cart response');
      }

      if (!response.ok || parsed.status) {
        document.dispatchEvent(
          new CartErrorEvent('lig-pair-feature', parsed.message, parsed.description, parsed.errors)
        );
        this.#reset(button);
        return;
      }

      const cart = await this.#fetchCart();
      const itemCount =
        typeof parsed.item_count === 'number'
          ? parsed.item_count
          : cart && typeof cart.item_count === 'number'
            ? cart.item_count
            : undefined;

      document.dispatchEvent(
        new CartAddEvent(cart ?? parsed, 'lig-pair-feature', {
          source: 'lig-pair-feature',
          itemCount,
          variantId,
          productId: button.dataset.productTitle,
          sections: parsed.sections,
        })
      );

      this.#openCartDrawer();

      button.dataset.added = 'true';
      this.#reset(button);
    } catch (error) {
      this.#reset(button);
    }
  }

  #reset(button) {
    button.removeAttribute('aria-busy');
    button.disabled = false;
  }
}

if (!customElements.get('lig-pair-feature')) {
  customElements.define('lig-pair-feature', LigPairFeature);
}
